import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/chillnn/cn";

import CloseIcon from "@/components/ui/icon/cross";

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Viewport>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Viewport
		ref={ref}
		className={cn(
			"fixed top-0 z-[10001]",
			"flex max-h-screen w-full flex-col-reverse gap-y-4 p-4",
			"sm:inset-x-0 sm:bottom-auto sm:top-5 sm:mx-auto sm:flex-col",
			"md:max-w-[420px]",
			className,
		)}
		{...props}
	/>
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
	cn(
		"group pointer-events-auto relative flex w-full items-center justify-between gap-x-4 overflow-hidden rounded-md border border-zinc-200 pl-5 py-7 pr-7 shadow-lg transition-all",
		"data-[swipe=cancel]:translate-y-0",
		"data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)] data-[swipe=move]:transition-none",
		"data-[swipe=end]:translate-y-[var(--radix-toast-swipe-end-y)] data-[swipe=end]:animate-out",
		"data-[state=open]:animate-in data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-top-full",
		"data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-top-full",
	),
	{
		variants: {
			variant: {
				default: "border bg-white text-zinc-950",
				error: "error bg-white",
				gift: "border bg-white text-zinc-950",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

const Toast = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Root>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
	// toast の表示時間
	const duration = variant === "error" ? 20000 : 5000;

	return (
		<ToastPrimitives.Root
			ref={ref}
			className={cn(toastVariants({ variant }), className)}
			duration={duration}
			{...props}
		/>
	);
});
Toast.displayName = ToastPrimitives.Root.displayName;

/**
 * Toast 内のアクションボタン
 */
const ToastAction = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Action>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Action
		ref={ref}
		className={cn(
			"inline-flex min-h-8 w-24 shrink-0 items-center justify-center rounded-md border border-zinc-200 px-3 text-sm font-medium ring-offset-white transition-colors",
			"hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
			// "group-[.error]:border-error-200 group-[.error]:hover:border-error-200/30 group-[.error]:hover:bg-error-200 group-[.error]:hover:text-error-600 group-[.error]:focus:ring-error-200",
			className,
		)}
		{...props}
	/>
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

/**
 * Toast 内のクローズボタン
 */
const ToastClose = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Close>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Close
		ref={ref}
		className={cn(
			"absolute right-2 top-2 rounded-md p-0.5 text-zinc-950/50 opacity-0 transition-opacity",
			"hover:text-zinc-950 focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100",
			// "group-[.error]:text-error-600 group-[.error]:hover:text-error-600 group-[.error]:focus:ring-error-200 group-[.error]:focus:ring-offset-error-200",
			className,
		)}
		toast-close=""
		{...props}
	>
		<CloseIcon size={"sm"} />
	</ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

/**
 * Toast 内のタイトル
 */
const ToastTitle = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Title>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Title ref={ref} className={cn("text-sm font-semibold", className)} {...props} />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

/**
 * Toast 内の説明
 */
const ToastDescription = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Description>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Description ref={ref} className={cn("text-sm opacity-90", className)} {...props} />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
	Toast,
	ToastAction,
	ToastClose,
	ToastDescription,
	ToastProvider,
	ToastTitle,
	ToastViewport,
	type ToastActionElement,
	type ToastProps,
};
